import { useMemo } from 'react';
import { LOOKUP_GROUP_CODES, ERROR_EFFECT_VALUES, DEFAULT_VALUES } from '../constants/formConstants';

const toNumberOrNull = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const toIsoOrNull = (d) => (d ? new Date(d).toISOString() : null);
const toMenzilTypeOrDefault = (value) => {
    if (value === 1 || value === '1' || value === 'KM' || value === 'km') return 1;
    if (value === 2 || value === '2' || value === 'Mil' || value === 'mil') return 2;
    return 1;
};
const toNumberList = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => Number(item)).filter(Number.isFinite);
    }
    if (value === null || value === undefined || value === '') {
        return [];
    }

    const normalized = Number(value);
    return Number.isFinite(normalized) ? [normalized] : [];
};
const normalizeDropdownId = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    if (!normalized) return null;
    const asNumber = Number(normalized);
    return Number.isNaN(asNumber) ? normalized : asNumber;
};

export const useFormDataMapper = () => {
    const mapRequestToForm = useMemo(() => {
        return (request, userParams) => {
            if (!request) return null;

            const lookupItems = request.lookupItems || [];

            // Arıza kodları lookup'ları
            const faultMainCategoryItem = lookupItems.find((i) => i.groupCode === LOOKUP_GROUP_CODES.FAULT_MAIN_CATEGORY);
            const faultSecondCategoryItem = lookupItems.find((i) => i.groupCode === LOOKUP_GROUP_CODES.FAULT_SECOND_CATEGORY);
            const faultSubCategoryItem = lookupItems.find((i) => i.groupCode === LOOKUP_GROUP_CODES.FAULT_SUB_CATEGORY);
            const faultSubPartCategoryItem = lookupItems.find((i) => i.groupCode === LOOKUP_GROUP_CODES.FAULT_SUB_PART_CATEGORY);
            const faultFailureModeItem = lookupItems.find((i) => i.groupCode === LOOKUP_GROUP_CODES.FAULT_FAILURE_MODE);

            // Backend'den gelen code, CascadingDropdowns API'sindeki lookupCode ile eşleşir
            // Ancak component value matching'de option.id kullandığı için direkt eşleştirme yapamıyoruz
            // Bu yüzden backend code'unu olduğu gibi displayValue olarak kullanıyoruz
            // Component yüklendiğinde API'den gelen options ile eşleştirmeyi yapacak
            const buildDisplayValue = (code, name) => {
                if (!code) return '';
                return name ? `${code} - ${name}` : String(code);
            };

            const comments = request.technicalSupportComments || [];
            const mappedChat = comments.map((comment) => ({
                id: comment.id,
                createdBy: comment.createdBy ?? comment.CreatedBy ?? comment.createdUserId ?? comment.CreatedUserId ?? null,
                userName: comment.createdName || 'Unknown',
                message: comment.comment || '',
                timestamp: comment.createdDate || new Date().toISOString(),
                type: comment.type || 1
            }));

            const expenseVal = request.outsorcingExpense ?? request.outsourcingExpense ?? null;
            const initialDealerId = normalizeDropdownId(request.bosBranchCode ?? request.BosBranchCode);
            const initialDealerAddressId = normalizeDropdownId(request.bosBranchSiteId ?? request.BosBranchSiteId ?? request.bosBranchSiteCode ?? request.BosBranchSiteCode);
            const initialDealerName = request.dealerName ?? request.DealerName ?? '';

            const warrantyPolicyValue = request.warrantyPolicy;
            const moreChassisValue = !!request.moreChassis;

            // Ana şasi politikası artık backend'den gelir (MainChassisPolicy alanı)
            const mainChassisPolicy = request.mainChassisPolicy !== undefined && request.mainChassisPolicy !== null ? Number(request.mainChassisPolicy) : 0;

            // Sadece ek şasi politikası WarrantyPolicy alanından gelir
            const additionalChassisAccepted = warrantyPolicyValue === 1 && moreChassisValue;

            return {
                submitted: false,
                errorNotificationDate: request.errorNotificationDate ? new Date(request.errorNotificationDate) : new Date(),
                userName: request.userNameSurname || request.userName || userParams?.userEmail || '',
                dealerName: initialDealerName || userParams?.dealerFullName || '',
                selectedDealer: initialDealerId
                    ? {
                          id: initialDealerId,
                          name: initialDealerName,
                          displayValue: initialDealerName,
                          erpDealerId: initialDealerId
                      }
                    : null,
                selectedDealerAddress: initialDealerAddressId
                    ? {
                          id: initialDealerAddressId,
                          addrId: initialDealerAddressId
                      }
                    : null,
                technicalResponsibleUserId: toNumberOrNull(request.technicalResponsibleUserId ?? request.TechnicalResponsibleUserId),
                chassisNo: request.chassisNo || '',
                vehicleWarrantyInf: request.vehicleWarrantyInf ? parseInt(request.vehicleWarrantyInf, 10) : null,
                vehicleModelEmissions: request.vehicleModelEmissions || request.VehicleModelEmissions || '',
                modelYear: request.modelYear ?? '',
                vehicleEmissions: request.vehicleEmission !== null && request.vehicleEmission !== undefined && request.vehicleEmission !== '' ? Number(request.vehicleEmission) : null,
                vehicleGsr: request.vehicleGsr !== null && request.vehicleGsr !== undefined && request.vehicleGsr !== '' ? Number(request.vehicleGsr) : null,
                kmInformation: request.kmInformation !== null && request.kmInformation !== undefined ? Number(request.kmInformation) : null,
                menzilType: toMenzilTypeOrDefault(request.menzilType ?? request.MenzilType),
                vehicleCustomerInf: request.vehicleCustomerInf || '',
                errorDescription: request.errorDescription || '',
                checksPerformed: request.checksPerformed || '',
                errorEffect: request.errorEffect !== null && request.errorEffect !== undefined ? Number(request.errorEffect) : null,
                isVor: request.isVor === 1 || request.isVor === true,
                mainCategory: buildDisplayValue(faultMainCategoryItem?.code, faultMainCategoryItem?.text),
                mainCategoryName: faultMainCategoryItem?.text || '',
                secondCategory: buildDisplayValue(faultSecondCategoryItem?.code, faultSecondCategoryItem?.text),
                secondCategoryName: faultSecondCategoryItem?.text || '',
                subCategory: buildDisplayValue(faultSubCategoryItem?.code, faultSubCategoryItem?.text),
                subCategoryName: faultSubCategoryItem?.text || '',
                subPartCategory: buildDisplayValue(faultSubPartCategoryItem?.code, faultSubPartCategoryItem?.text),
                subPartCategoryName: faultSubPartCategoryItem?.text || '',
                failureMode: faultFailureModeItem?.code ? Number(faultFailureModeItem.code) : null,
                failureModeName: faultFailureModeItem?.text || '',
                cluster: request.cluster ?? request.Cluster ?? request.softwareVersion ?? '',
                evcu: request.evcu ?? request.Evcu ?? '',
                zr5: request.zr5 ?? request.Zr5 ?? '',
                problemSolved: request.problemSolved === 1 || request.problemSolved === true,
                solutionMethod: request.solutionMethod || '',
                solutionDate: request.solutionDate ? new Date(request.solutionDate) : null,
                solutionDateFromStatus: false,
                statusBeforeSolved: null,
                onSiteSupport: request.onSiteSupport === 1 || request.onSiteSupport === true,
                onSiteSupportReason: toNumberList(request.onSiteSupportReason ?? request.OnsiteSupportList ?? request.onsiteSupportList),
                technicalServiceDeclaration: toNumberList(request.technicalServiceDeclaration ?? request.DeclarationControlList ?? request.declarationControlList),
                technicalPolicy: request.technicalPolicy === 1 || request.technicalPolicy === true,
                visitDate: request.visitDate ? new Date(request.visitDate) : null,
                visitErrorDesc: request.visitErrorDesc || '',
                rootCause: request.rootCause || '',
                solution: request.solution || '',
                manHour: toNumberOrNull(request.manHour),
                outsourcingExpense: toNumberOrNull(expenseVal),
                totalExpenses: toNumberOrNull(request.totalExpenses),
                expenseCurr: request.expenseCurr ?? request.ExpenseCurr ?? null,
                ptsCriteria: request.ptsCriteria === 1 || request.ptsCriteria === true,
                ptsNumber: request.ptsNumber || '',
                statusNotification: request.statusNotification ?? null,
                statusDate: request.statusDate ? new Date(request.statusDate) : null,
                statusChanged: false,
                actionManager: request.actionManager ?? null,
                actionManagerChanged: false,
                appointmentTechnResponsible: request.appointmentTechnResponsible || '',
                totalSolutionTime: request.totalSolutionTime || '',
                seeCommunicationMovements: request.seeCommunicationMovements || '',
                moreChassis: moreChassisValue,
                additionalChassis: Array.isArray(request.chassisList) ? request.chassisList : [],
                chatHistory: mappedChat,
                newMessage: '',
                hasScrolledToBottom: !!request.technicalPolicy,
                policyDialogVisible: false,
                warrantyPolicyDialogVisible: false,
                hasScrolledToBottomWarranty: false,
                initialChassisNo: request.chassisNo || '',
                additionalChassisHasOutOfWarranty: false,
                mainChassisPolicy,
                additionalChassisWarrantyPolicyAccepted: additionalChassisAccepted
            };
        };
    }, []);

    const mapFormToPayload = useMemo(() => {
        return (form, editMode, request, userParams, isVorMode) => {
            const lookupItems = [];

            // Arıza kodları için lookup'lar ekle
            // CascadingDropdowns "ID - Name" formatında değer döndürür, sadece ID kısmını al
            const extractIdFromDisplayValue = (value) => {
                if (!value) return '';
                const strValue = String(value);
                const match = strValue.match(/^(\d+)/);
                if (!match) return '';
                return String(Number(match[1])); // Numeric'e çevirip tekrar string'e çevir (leading zeros temizler)
            };

            if (form.mainCategory) {
                const code = extractIdFromDisplayValue(form.mainCategory);
                lookupItems.push({
                    groupCode: LOOKUP_GROUP_CODES.FAULT_MAIN_CATEGORY,
                    code: String(code),
                    text: form.mainCategoryName || String(code)
                });
            }
            if (form.secondCategory) {
                const code = extractIdFromDisplayValue(form.secondCategory);
                lookupItems.push({
                    groupCode: LOOKUP_GROUP_CODES.FAULT_SECOND_CATEGORY,
                    code: String(code),
                    text: form.secondCategoryName || String(code)
                });
            }
            if (form.subCategory) {
                const code = extractIdFromDisplayValue(form.subCategory);
                lookupItems.push({
                    groupCode: LOOKUP_GROUP_CODES.FAULT_SUB_CATEGORY,
                    code: String(code),
                    text: form.subCategoryName || String(code)
                });
            }
            if (form.subPartCategory) {
                const code = extractIdFromDisplayValue(form.subPartCategory);
                lookupItems.push({
                    groupCode: LOOKUP_GROUP_CODES.FAULT_SUB_PART_CATEGORY,
                    code: String(code),
                    text: form.subPartCategoryName || String(code)
                });
            }
            if (form.failureMode) {
                lookupItems.push({
                    groupCode: LOOKUP_GROUP_CODES.FAULT_FAILURE_MODE,
                    code: String(form.failureMode),
                    text: form.failureModeName || String(form.failureMode)
                });
            }

            const selectedDealerId = form.selectedDealer?.id ?? form.selectedDealer?.erpDealerId ?? null;
            const selectedDealerAddressId =
                form.selectedDealerAddress?.id ??
                form.selectedDealerAddress?.addrId ??
                form.selectedDealerAddress?.erpSiteUseId ??
                (typeof form.selectedDealerAddress === 'string' || typeof form.selectedDealerAddress === 'number' ? form.selectedDealerAddress : null);

            const fallbackDealerId = userParams?.dealerId ?? userParams?.DealerId ?? userParams?.bosBranchCode ?? userParams?.BosBranchCode ?? '';
            const fallbackSiteUseId = userParams?.siteUseId ?? userParams?.SiteUseId ?? userParams?.bosBranchSiteId ?? userParams?.BosBranchSiteId ?? userParams?.bosBranchSiteCode ?? userParams?.BosBranchSiteCode ?? '';

            const resolvedBosBranchCode = selectedDealerId ? String(selectedDealerId) : fallbackDealerId ? String(fallbackDealerId) : '';
            const resolvedBosBranchSiteId = selectedDealerId ? (selectedDealerAddressId ? String(selectedDealerAddressId) : '') : fallbackSiteUseId ? String(fallbackSiteUseId) : '';
            const resolvedDealerName = form.selectedDealer?.name || form.selectedDealer?.text || form.selectedDealer?.displayValue || form.dealerName || userParams?.dealerFullName || '';

            const warrantyPolicyPayload = form.mainChassisPolicy === 1 || form.additionalChassisWarrantyPolicyAccepted ? 1 : 0;

            return {
                id: editMode && request?.id ? request.id : 0,
                errorNotificationDate: toIsoOrNull(form.errorNotificationDate),
                userNameSurname: form.userName || '',
                dealerName: resolvedDealerName,
                chassisNo: form.chassisNo || '',
                modelYear: toNumberOrNull(form.modelYear),
                vehicleWarrantyInf: form.vehicleWarrantyInf?.toString() || '',
                VehicleModelEmissions: form.vehicleModelEmissions || '',
                VehicleEmission: form.vehicleEmissions ?? null,
                vehicleModelEmissions: form.vehicleModelEmissions || '',
                vehicleGsr: form.vehicleGsr !== null && form.vehicleGsr !== undefined ? String(form.vehicleGsr) : '',
                kmInformation: form.kmInformation ?? 0,
                MenzilType: toMenzilTypeOrDefault(form.menzilType),
                vehicleCustomerInf: form.vehicleCustomerInf || '',
                errorDescription: form.errorDescription || '',
                checksPerformed: form.checksPerformed || '',
                errorEffect: (isVorMode ? ERROR_EFFECT_VALUES.VOR : form.errorEffect) ?? 0,
                isVor: form.isVor ? 1 : 0,
                moreChassis: !!form.moreChassis,
                chassisList: form.moreChassis ? form.additionalChassis : [],
                softwareVersion: [form.cluster, form.evcu, form.zr5].filter((item) => !!item?.trim()).join(' / '),
                Cluster: form.cluster || '',
                Evcu: form.evcu || '',
                Zr5: form.zr5 || '',
                problemSolved: !!form.problemSolved,
                solutionMethod: form.solutionMethod || '',
                solutionDate: toIsoOrNull(form.solutionDate),
                onSiteSupport: form.onSiteSupport ? 1 : 0,
                OnsiteSupportList: toNumberList(form.onSiteSupportReason),
                DeclarationControlList: toNumberList(form.technicalServiceDeclaration),
                technicalPolicy: form.technicalPolicy ? 1 : 0,
                visitDate: toIsoOrNull(form.visitDate),
                visitErrorDesc: form.visitErrorDesc || '',
                rootCause: form.rootCause || '',
                solution: form.solution || '',
                manHour: form.manHour !== null && form.manHour !== undefined ? String(form.manHour) : '',
                outsorcingExpense: form.outsourcingExpense !== null && form.outsourcingExpense !== undefined ? String(form.outsourcingExpense) : '',
                totalExpenses: form.totalExpenses !== null && form.totalExpenses !== undefined ? String(form.totalExpenses) : '',
                ExpenseCurr: form.expenseCurr ?? null,
                ptsCriteria: form.ptsCriteria ? 1 : 0,
                ptsNumber: form.ptsNumber || '',
                seeCommunicationMovements: form.seeCommunicationMovements || '',
                statusNotification: form.statusNotification ?? null,
                ...(form.statusChanged || form.actionManagerChanged ? { statusDate: toIsoOrNull(form.statusDate) } : {}),
                actionManager: form.actionManager ?? null,
                technicalResponsibleUserId: toNumberOrNull(form.technicalResponsibleUserId),
                appointmentTechnResponsible: form.appointmentTechnResponsible || '',
                totalSolutionTime: form.totalSolutionTime || '',
                status: DEFAULT_VALUES.STATUS,
                isActive: DEFAULT_VALUES.IS_ACTIVE,
                modifiedName: form.userName || '',
                lastUpdateSession: DEFAULT_VALUES.LAST_UPDATE_SESSION,
                BosBranchCode: resolvedBosBranchCode,
                BosBranchSiteCode: resolvedBosBranchSiteId,
                // Backward compatibility: keep legacy keys during transition.
                bosBranchCode: resolvedBosBranchCode,
                bosBranchSiteCode: resolvedBosBranchSiteId,
                lookupItems,
                WarrantyPolicy: warrantyPolicyPayload,
                MainChassisPolicy: form.mainChassisPolicy ?? 0
                // Not: Arıza kodları sadece lookupItems üzerinden gönderilir
                // MainCategory, SecondCategory vb. direkt alanlar backend tarafından kullanılmıyor
            };
        };
    }, []);

    return {
        mapRequestToForm,
        mapFormToPayload
    };
};
